import * as React from "react";
import { useContext, useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import { useParams, useHistory, Link } from "react-router-dom";

import { Popover, Tooltip } from "antd";
import { Page, PageHero, DataTable, Spinner } from "src/components/common";
import { Button, IconButton } from "src/components/buttons";
import { Icon } from "src/components/icons/Icon";
import { Lock, FileSpreadsheet, ClipboardCheck, SquarePen } from "lucide-react";

import { AdminPermissions, ClientPermissions } from "src/common/Permissions";
import AssessmentResultsAPI from "src/admin/results/AssessmentResultsAPI";
import SwitchFilter from "src/components/common/SwitchFilter";
import NeurapulseFilter from "src/components/common/NeurapulseFilter";
import LocationAPI from "src/admin/Organization/Location/LocationAPI";
import CustomFieldAPI from "src/admin/Organization/CustomFields/CustomFieldAPI";

import { CustomFields } from "src/common/CustomFields";
import PickListContext from "src/context/pickListContext";
import {
	setDateColumnCellValue,
	dateFilterMethod,
	DateFilter,
} from "src/components/forms/DateFilter";

import { CSVLink } from "react-csv";
import Dropdown from "react-bootstrap/Dropdown";
import ClientResultModalOverlay from "src/components/forms/ClientResultModalOverlay";
import OrganizationAPI from "src/admin/Organization/API/OrganizationAPI";
import {
	ModuleType,
} from "src/common/Enums";
import {
	getCustomFieldValues,
	getCustomFieldValidation,
	ICustomFieldValidation,
} from "src/admin/Organization/CustomFields/CustomFieldContainer";
import CustomProtocolHandler from "src/vitals/CustomProtocolHandler";
import Utils, { getTime } from "src/common/utils";
import DatasetAPI from "src/admin/Organization/Datasets/DatasetAPI";
import ClientDatasetAPI from "./ClientDatasetAPI";
import GenerateExceleRATEPdf from "src/admin/report/templates/v1/GenerateExceleRATEPdf";
import {
	ResultsHeaders,
} from "src/common/CsvExport";
import ClientAppConstants from "src/common/Constants";
import {
	setStatusCellValue,
	filterByStatus,
	StatusFilterMethod,
} from "src/components/forms/StatusFilter";
import { getCareScore } from "src/common/Formatters";
import {
	hasAnyExceleRATEProduct,
	hasAnyProduct,
	hasDriveABLEProduct,
	hasExceleRATEV1OnlyProduct,
} from "src/components/forms/ProductType";
import {
	inofficeRecommendationToolTip,
	inOfficeScoreToolTip,
	onroadScoreToolTip,
	overallScoreToolTip,
} from "src/components/forms/TestToolTip";
import {
	IModule,
	ModulesClient,
	Assessment,
} from "src/@types/ImpiricaPlatformApi";
import { toast } from "react-toastify";
import CoreAPI from "src/core/CoreAPI";

// Shared hooks
import { useResultsFilters } from "src/components/assessmentResults/hooks/useResultsFilters";
import { useVitalsHandler } from "src/components/assessmentResults/hooks/useVitalsHandler";
import { useReportGeneration } from "src/components/assessmentResults/hooks/useReportGeneration";

// Cell components
import { VitalsScoreCell } from "src/components/assessmentResults/cells/VitalsScoreCell";
import { VitalsRecommendationCell } from "src/components/assessmentResults/cells/VitalsRecommendationCell";
import { InofficeRecommendationCell } from "src/components/assessmentResults/cells/InofficeRecommendationCell";
import { CoreRecommendationCell } from "src/components/assessmentResults/cells/CoreRecommendationCell";
import { DoreRecommendationCell } from "src/components/assessmentResults/cells/DoreRecommendationCell";
import { NeurapulseResultCell } from "src/components/assessmentResults/cells/NeurapulseResultCell";

// Utils
import {
	getCsvData,
	getDatasetPermissions,
	canSeeScores,
	returnedAssessmentCustomFields,
	setMultipleLabelComponent,
	IGetCsvDataResult,
} from "src/components/assessmentResults/assessmentResultsUtils";

interface IProps {
	currentUser?: any;
	api?: any;
}

const DatasetAssessmentResults: React.FC<IProps> = ({ currentUser, api }) => {
	const context = useContext(PickListContext);
	const { organizationId, datasetId: datasetIdParam } = useParams<{
		organizationId: string;
		datasetId: string;
	}>();
	const datasetId = +datasetIdParam;
	const history = useHistory();

	// ─── Core data state ────────────────────────────────────────────────────────
	const [results, setResults] = useState<ClientSiteAssessmentResultsDTO[]>([]);
	const [dataset, setDataset] = useState<DatasetDTO>({} as DatasetDTO);
	const [locations, setLocations] = useState<any[]>([]);
	const [datasetLanguages, setDatasetLanguages] = useState<PickListItemResponseDTO[]>([]);
	const [customFields, setCustomFields] = useState<CustomFieldDTO[]>([]);
	const [datasetModules, setDatasetModules] = useState<IModule[]>([]);
	const [commercialDrivingExpSettings, setCommercialDrivingExpSettings] = useState<CommercialDrivingExpSettingsDTO>({} as CommercialDrivingExpSettingsDTO);

	// ─── UI state ───────────────────────────────────────────────────────────────
	const [isLoading, setIsLoading] = useState(true);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedAssessmentResult, setSelectedAssessmentResult] = useState<ClientSiteAssessmentResultsDTO | undefined>(undefined);
	const [scoring, setScoring] = useState(false);
	const [isCoreCertified, setIsCoreCertified] = useState(false);
	const [fetchDataError, setFetchDataError] = useState(false);
	const [initialDatasetId] = useState<number>(datasetId);

	// ─── Modal / test state ──────────────────────────────────────────────────────
	const [newTestCustomFieldValues, setNewTestCustomFieldValues] = useState<CustomFieldValueDTO[]>([]);
	const [newTestPurposeId, setNewTestPurposeId] = useState<number | undefined>(undefined);
	const [validNewTestValidation, setValidNewTestValidation] = useState<string[]>([]);

	// ─── CSV state ───────────────────────────────────────────────────────────────
	const [csvTestResults, setCsvTestResults] = useState<IGetCsvDataResult>({ headers: [], csvResults: [] });
	const [csvTestResultsFiltered, setCsvTestResultsFiltered] = useState<IGetCsvDataResult>({ headers: [], csvResults: [] });

	// ─── Permissions ─────────────────────────────────────────────────────────────
	const datasetPermissions = getDatasetPermissions(currentUser, datasetId);
	const userCanSeeScores = canSeeScores(currentUser, datasetPermissions);

	// ─── Filter hook ─────────────────────────────────────────────────────────────
	const {
		filterState,
		filtered,
		setFiltered,
		setFilterValue,
		setNeurapulseFilterValue,
		setFilterMethodForSwitch,
		setFilterMethodForNeurapulseSwitch,
		showOrHideDisplayFilter,
		closeFilter,
		clearFilters,
		onDateChange,
	} = useResultsFilters(userCanSeeScores);

	// ─── onPollComplete — uses ref to break circularity with vitals hook ─────────
	const onPollCompleteRef = useRef<(vitalsRunning: boolean) => void>(() => {});

	// ─── Vitals hook ─────────────────────────────────────────────────────────────
	const {
		vitalsLaunched,
		DCATButtonClicked,
		setDCATButtonClicked,
		DCATRunning,
		setDCATRunning,
		DCATerror,
		setDCATerror,
		canSync,
		setCanSync,
		syncSrc,
		setSyncSrc,
		showLaunchMessageVisible,
		vitalsTestResultId,
		setVitalsTestResultId,
		options,
		setVitalsLaunched,
		handleCustomProtocolError,
		startVitalsTest: startVitalsTestBase,
		resumeVitalsTest: resumeVitalsTestBase,
		initCanSync,
	} = useVitalsHandler({ currentUser, onPollComplete: (v) => onPollCompleteRef.current(v) });

	// Wire up onPollComplete — navigate to vitals test result after poll finishes
	onPollCompleteRef.current = useCallback(
		(vitalsRunning: boolean) => {
			if (!vitalsRunning && selectedAssessmentResult && vitalsTestResultId) {
				history.push(
					`/organizations/${organizationId}/datasets/${datasetId}/clients/${selectedAssessmentResult.clientId}/sessionId/${selectedAssessmentResult.id}/vitalsTestResults/${vitalsTestResultId}`
				);
			}
		},
		[history, organizationId, datasetId, selectedAssessmentResult, vitalsTestResultId]
	);

	// ─── Report generation hook ───────────────────────────────────────────────────
	const { fetchReportData, isGeneratingPdf } = useReportGeneration();

	// ─── React table ref ──────────────────────────────────────────────────────────
	const reactTable = useRef<DataTable>(null);

	// ─── Data loading ─────────────────────────────────────────────────────────────
	const getClientDatasetModules = useCallback(async (): Promise<IModule[]> => {
		const modulesClient: ModulesClient = api.modulesClient;
		return modulesClient.getAllModulesForDatasetPermissions(
			+organizationId,
			datasetId,
			currentUser.id
		);
	}, [api, organizationId, datasetId, currentUser]);

	const loadData = useCallback(async () => {
		try {
			if (
				currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
				currentUser.vitalsCertified
			) {
				setCanSync(true);
			}

			if (
				currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
				currentUser.coreCertified
			) {
				setIsCoreCertified(true);
			}

			const p_dataset = ClientDatasetAPI.getDatset({
				organizationId: +organizationId,
				datasetId,
			});

			const p_assessmentResults = AssessmentResultsAPI.getDatasetAssessmentResults({
				datasetId,
			});
			const p_locations = LocationAPI.getClientSiteOrganizationLocations(
				+organizationId,
				datasetId
			);
			const p_customFields = CustomFieldAPI.getOrganizationCustomFields(
				+organizationId,
				datasetId
			);
			const p_datasetLanguages = DatasetAPI.getDatasetLanguages({
				organizationId: +organizationId,
				datasetId,
			});
			const p_commercialDrivingExpSettings =
				OrganizationAPI.getCommercialDrivingExpSettings({
					tenantId: +organizationId,
					datasetId,
				});

			const datasetModulesData = await getClientDatasetModules();
			initCanSync(currentUser);

			Promise.all([
				p_dataset,
				p_assessmentResults,
				p_locations,
				p_customFields,
				p_datasetLanguages,
				p_commercialDrivingExpSettings,
			]).then((values) => {
				const [
					datasetData,
					assessmentResults,
					locationData,
					customFieldsData,
					datasetLanguagesData,
					commercialDrivingExpSettingsData,
				] = values;

				const filteredCustomFields = returnedAssessmentCustomFields(
					customFieldsData,
					context.pickListItems,
					datasetId
				);

				if (assessmentResults && locationData) {
					setResults(assessmentResults);
					setLocations(locationData);
					setCustomFields(filteredCustomFields);
					setDataset(datasetData);
					setDatasetLanguages(datasetLanguagesData);
					setDatasetModules(datasetModulesData);
					setCommercialDrivingExpSettings(commercialDrivingExpSettingsData);

					const csvData = getCsvData(assessmentResults, ResultsHeaders, datasetData, true);
					setCsvTestResults(csvData);
					setCsvTestResultsFiltered(csvData);
				}
			}).finally(() => setIsLoading(false));
		} catch (error) {
			setFetchDataError(true);
			setIsLoading(false);
		}
	}, [
		organizationId,
		datasetId,
		currentUser,
		context.pickListItems,
		getClientDatasetModules,
		setCanSync,
		initCanSync,
	]);

	useEffect(() => {
		setFiltered([{ id: "clientIsActive", value: "Active" }]);
		loadData();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// ─── Unique callbacks ─────────────────────────────────────────────────────────
	const onAssessmentCreated = useCallback(
		async (assessment: Assessment) => {
			const newAssessmentDto = Utils.mapClientSiteAssessmentResult(assessment);
			setSelectedAssessmentResult(newAssessmentDto);
			await loadData();
		},
		[loadData]
	);

	const onCustomFieldValuesUpdated = useCallback(async () => {
		await loadData();
	}, [loadData]);

	// ─── Helpers ──────────────────────────────────────────────────────────────────
	const onDataFiltered = useCallback(
		(data: any) => {
			const filteredData = getCsvData(data as ClientSiteAssessmentResultsDTO[], ResultsHeaders, dataset, true);
			setCsvTestResultsFiltered(filteredData);
		},
		[dataset]
	);

	const convertDisplayOnValuesToLabelComponent = useCallback((row: any) => {
		const labelValue =
			row.value !== undefined && row.value !== "" ? row.value.toString() : null;
		if (labelValue !== null) {
			const displayValues = labelValue.split(",").join(", ");
			return setMultipleLabelComponent([displayValues]);
		}
		return "";
	}, []);

	const clearAll = useCallback(() => {
		clearFilters();
		const csvData = getCsvData(results, ResultsHeaders, dataset, true);
		setCsvTestResults(csvData);
		setCsvTestResultsFiltered(csvData);
	}, [clearFilters, results, dataset]);

	// ─── Assessment helpers ────────────────────────────────────────────────────────
	const enableEditAssessment = useCallback(
		(assessmentResult: ClientSiteAssessmentResultsDTO): boolean => {
			const dp = getDatasetPermissions(currentUser, datasetId);
			if (assessmentResult.dataLocked) return false;
			if (
				dp &&
				dp.permissions &&
				!dp.permissions.includes(ClientPermissions.EditCoreTest) &&
				!dp.permissions.includes(ClientPermissions.EditVitalsTest) &&
				!currentUser.permissions!.includes(AdminPermissions.GlobalAdmin)
			) {
				return false;
			}
			return hasAnyProduct(dataset.productId);
		},
		[currentUser, datasetId, dataset]
	);

	const toggleOverlayOpen = useCallback(
		(assessmentResult: ClientSiteAssessmentResultsDTO) => {
			setIsModalOpen((prev) => !prev);
			setSelectedAssessmentResult(assessmentResult);
			setVitalsTestResultId(assessmentResult.vitalsTestResultId);
		},
		[setVitalsTestResultId]
	);

	const closeModal = useCallback(() => {
		setIsModalOpen(false);
		setSyncSrc(undefined);
		setDCATButtonClicked(false);
		setDCATRunning(false);
	}, [setSyncSrc, setDCATButtonClicked, setDCATRunning]);

	// ─── Custom field helpers ────────────────────────────────────────────────────
	const checkIfCustomFieldValueIsSuccess = useCallback(
		(validCustomFields: string[]): boolean => {
			return validCustomFields.every((v) => v === "success");
		},
		[]
	);

	const postAssessmentCustomFields = useCallback(() => {
		if (!selectedAssessmentResult) return;
		if (newTestCustomFieldValues && newTestCustomFieldValues.length > 0) {
			AssessmentResultsAPI.postAssessmentResultCustomFields({
				datasetId,
				assessmentResultId: selectedAssessmentResult.id,
				assessmentResultCustomFieldValues: newTestCustomFieldValues,
			});
		}
	}, [selectedAssessmentResult, newTestCustomFieldValues, datasetId]);

	const postAssessmentPurpose = useCallback(() => {
		if (!selectedAssessmentResult) return;
		if (
			selectedAssessmentResult.assessmentPurposeId &&
			selectedAssessmentResult.assessmentPurposeId > 0
		) {
			AssessmentResultsAPI.postAssessmentResultPurpose({
				datasetId,
				assessmentResultId: selectedAssessmentResult.id,
				assessmentPurposeId: selectedAssessmentResult.assessmentPurposeId,
			});
		}
	}, [selectedAssessmentResult, datasetId]);

	// ─── Navigation helpers ────────────────────────────────────────────────────────
	const getCoreTestResult = useCallback(async () => {
		const coreTestId = selectedAssessmentResult!.coreTestResultId;
		const clientId = selectedAssessmentResult!.clientId;

		const coreEvaluators = await OrganizationAPI.getDatasetEvaluators({
			tenantId: +organizationId,
			datasetId,
		});
		const selectedEvaluator = await OrganizationAPI.getEvaluator({
			tenantId: +organizationId,
			datasetId,
			evaluatorId: selectedAssessmentResult!.coreAssessorId
				? selectedAssessmentResult!.coreAssessorId
				: 0,
		});
		if (
			selectedEvaluator.id === undefined &&
			coreEvaluators.length === 0 &&
			coreEvaluators.every((ce: any) => !ce.isActive!)
		) {
			toast.error("No active evaluator found, please contact administrator");
			return;
		}

		postAssessmentCustomFields();
		postAssessmentPurpose();

		history.push({
			pathname: `/organizations/${organizationId}/datasets/${datasetId}/clients/${clientId}/sessionId/${
				selectedAssessmentResult!.id
			}/core/${coreTestId}/general-appraisal`,
		});
	}, [
		selectedAssessmentResult,
		organizationId,
		datasetId,
		history,
		postAssessmentCustomFields,
		postAssessmentPurpose,
	]);

	const getVitalsTestResult = useCallback(() => {
		const clientId = selectedAssessmentResult!.clientId;
		postAssessmentCustomFields();
		postAssessmentPurpose();

		history.push({
			pathname: `/organizations/${organizationId}/datasets/${datasetId}/clients/${clientId}/sessionId/${
				selectedAssessmentResult!.id
			}/vitalsTestResults/${vitalsTestResultId}`,
		});
	}, [
		selectedAssessmentResult,
		vitalsTestResultId,
		organizationId,
		datasetId,
		history,
		postAssessmentCustomFields,
		postAssessmentPurpose,
	]);

	const startCoreTest = useCallback(async () => {
		const clientId = selectedAssessmentResult && selectedAssessmentResult.clientId;
		try {
			const res = await CoreAPI.checkActiveCoursesAndLocationsForCoreTest({
				organizationId: +organizationId,
				datasetId,
				clientId,
			});
			if (!res) {
				toast.error("No Active courses in Active Locations found, please contact administrator");
				return;
			}

			const coreEvaluators = await OrganizationAPI.getDatasetEvaluators({
				tenantId: +organizationId,
				datasetId,
			});
			if (
				coreEvaluators.length === 0 &&
				coreEvaluators.every((ce: any) => !ce.isActive!)
			) {
				toast.error("No active CORE evaluators found, please contact administrator");
				return;
			}

			const newTest = {
				datasetId: Number(datasetId),
				clientId: Number(clientId),
				coreGeneralSubmitted: false,
				dateAdministered: moment().format("MMM DD, YYYY"),
				id: undefined,
				dataLocked: true,
				coreTestCustomFieldValues: [],
				assessmentResultCustomFieldValues: newTestCustomFieldValues,
				startFromAssessmentId: selectedAssessmentResult
					? selectedAssessmentResult.id
					: undefined,
				assessmentPurposeId: selectedAssessmentResult
					? selectedAssessmentResult.assessmentPurposeId
					: newTestPurposeId,
			} as UpsertCoreTestResultRequest;

			const coreTest = await CoreAPI.upsertCoreTest(newTest);
			const sessionId = coreTest.sessionId
				? coreTest.sessionId
				: selectedAssessmentResult!.id;

			history.push(
				`/organizations/${organizationId}/datasets/${datasetId}/clients/${clientId}/sessionId/${sessionId}/core/${coreTest.id!}/general-appraisal`
			);
		} catch (error) {
			toast.error("Something went wrong");
		}
	}, [
		organizationId,
		datasetId,
		newTestCustomFieldValues,
		selectedAssessmentResult,
		newTestPurposeId,
		history,
	]);

	const startVitalsTest = useCallback(() => {
		startVitalsTestBase({
			datasetId,
			clientId: (selectedAssessmentResult && selectedAssessmentResult.clientId) || 0,
			datasetLanguages: datasetLanguages || [],
			newTestCustomFieldValues,
			newTestPurposeId,
			selectedAssessmentResult,
		});
	}, [
		startVitalsTestBase,
		datasetId,
		selectedAssessmentResult,
		datasetLanguages,
		newTestCustomFieldValues,
		newTestPurposeId,
	]);

	const resumeVitalsTest = useCallback(() => {
		resumeVitalsTestBase({
			datasetId,
			datasetLanguages: datasetLanguages || [],
			vitalsTestResultId,
		});
	}, [resumeVitalsTestBase, datasetId, datasetLanguages, vitalsTestResultId]);

	const navigateToVitalsSummary = useCallback(
		(sessionId: number | undefined, careTestResultId: number | undefined) => {
			if (careTestResultId) {
				history.push(
					`/organizations/${organizationId}/datasets/${datasetId}/sessionid/${sessionId}/vitalsm/${careTestResultId}/productid/${dataset.productId}/summary`
				);
			}
		},
		[history, organizationId, datasetId, dataset]
	);

	const scoreAssessment = useCallback(
		async (assessmentId: number) => {
			try {
				const { scoringClient } = api;
				await scoringClient.scoreAssessment(assessmentId);
				toast.success("Score session request complete");
				loadData();
				setScoring(false);
			} catch {
				toast.error("Failed to score session");
			}
		},
		[api, loadData]
	);

	const getNewTestAssessmentCustomFieldValidation = useCallback(
		(validation: ICustomFieldValidation[]) => {
			const validCustomFieldValues = getCustomFieldValidation(validation);
			const statuses = validCustomFieldValues.map((c) => {
				if (c.validationStatus === undefined) return "";
				return c.validationStatus;
			});
			setValidNewTestValidation(statuses);
		},
		[]
	);

	const getAssessmentCustomFieldValues = useCallback(
		(customFieldValues: CustomFieldValueDTO[]) => {
			const values = getCustomFieldValues(customFieldValues);
			if (values.length > 0) {
				setNewTestCustomFieldValues(values);
			}
		},
		[]
	);

	// ─── getColumns ───────────────────────────────────────────────────────────────
	const getColumns = useCallback(
		(cols: CustomFieldDTO[]) => {
			const customColumns = CustomFields.renderCustomFieldColumn(
				cols,
				convertDisplayOnValuesToLabelComponent
			);

			const columns = [
				{
					Header: <Tooltip title="Web Id">Web Id</Tooltip>,
					accessor: "webId",
					filterable: true,
				},
				{
					Header: <Tooltip title="Date Created">Date Created</Tooltip>,
					accessor: "dateCreated",
					Cell: setDateColumnCellValue,
					filterMethod: dateFilterMethod("dateCreated"),
					Filter: ({ onChange }: any) => (
						<DateFilter
							accessor="dateCreated"
							onDateChange={onDateChange}
							date={filterState.dateCreated}
							onChange={onChange}
						/>
					),
					filterable: true,
				},
				{
					Header: <Tooltip title="Participant Name">Participant Name</Tooltip>,
					accessor: "clientName",
					show:
						dataset.showClientFirstName &&
						dataset.showClientLastName,
					filterable: true,
					style: {
						whiteSpace: "pre-wrap",
					},
					Cell: (rowInfo: any) => {
						const clientId = rowInfo.original.clientId;
						const linkUrl = `/organizations/${organizationId}/datasets/${datasetId}/clients/${clientId}/results`;
						return <Link to={linkUrl}>{rowInfo.value}</Link>;
					},
				},
				{
					Header: (
						<Tooltip title="Employee Id">
							{dataset.productId === "ExceleRATEV2" ? "Employee Id" : "Id"}
						</Tooltip>
					),
					accessor: "employeeId",
					show: dataset.showClientParticipantId,
					filterable: true,
				},
				{
					Header: (
						<Tooltip title="Participant Status">Participant Status</Tooltip>
					),
					accessor: "clientIsActive",
					Cell: setStatusCellValue,
					filterMethod: filterByStatus("clientIsActive"),
					Filter: StatusFilterMethod,
				},
				{
					Header: overallScoreToolTip(dataset.productId),
					show: hasAnyProduct(dataset.productId),
					filterable: true,
					accessor: "excelerateScore",
					maxWidth: 80,
					Cell: (rowInfo: any) => {
						if (rowInfo.row.excelerateScore) {
							return rowInfo.row.excelerateScore;
						} else if (
							(rowInfo.original.coreScoreCompleted &&
								rowInfo.original.vitalsScoreCompleted) ||
							(rowInfo.original.coreScoreCompleted &&
								rowInfo.original.careScoreCompleted)
						) {
							return "C";
						} else {
							return "NC";
						}
					},
				},
				{
					Header: inOfficeScoreToolTip(dataset.productId),
					show: !hasExceleRATEV1OnlyProduct(dataset.productId),
					accessor: "careScore",
					maxWidth: 80,
					Cell: (rowInfo: any) =>
						getCareScore(
							rowInfo.row.careScore,
							rowInfo.original.careScoreCompleted,
							rowInfo.original.careTestResultId
						),
				},
				{
					Header: inofficeRecommendationToolTip(dataset.productId),
					show: !hasExceleRATEV1OnlyProduct(dataset.productId),
					accessor: "careRecommendation",
					Cell: (rowInfo: any) => (
						<InofficeRecommendationCell result={rowInfo.original} />
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							closeDisplayFilter={closeFilter}
							convertForFilter={setFilterValue}
							displayFilterName="displayInofficeRecommendationFilters"
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							shouldDisplayFilter={
								filterState.displayFilters.displayInofficeRecommendationFilters &&
								filterState.showFilters
							}
							onChange={onChange}
							name="inofficeRecommendationStatus"
							values={filterState.inofficeRecommendationStatus}
						/>
					),
					filterMethod: (filter: any, row: any) =>
						setFilterMethodForSwitch(filter, row),
				},
				{
					Header: <Tooltip title="VITALS">VITALS</Tooltip>,
					show: hasExceleRATEV1OnlyProduct(dataset.productId),
					maxWidth: 80,
					accessor: "vitalsScore",
					filterable: true,
					Cell: (rowInfo: any) => (
						<VitalsScoreCell result={rowInfo.original} canSeeScores={userCanSeeScores} />
					),
				},
				{
					Header: (
						<Tooltip title="VITALS Recommendation">VITALS RECOMMENDATION</Tooltip>
					),
					show: hasExceleRATEV1OnlyProduct(dataset.productId),
					accessor: "vitalsStatusId",
					filterable: true,
					Cell: (rowInfo: any) => (
						<VitalsRecommendationCell result={rowInfo.original} />
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							closeDisplayFilter={closeFilter}
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							displayFilterName="displayVitalsFilters"
							shouldDisplayFilter={filterState.displayFilters.displayVitalsFilters}
							convertForFilter={setFilterValue}
							onChange={onChange}
							name="vitalsStatus"
							values={filterState.vitalsStatus}
						/>
					),
					filterMethod: (filter: any, row: any) =>
						setFilterMethodForSwitch(filter, row),
				},
				{
					Header: (
						<Tooltip title="C-Error">
							<span style={{ whiteSpace: "nowrap" }}>C-Error</span>
						</Tooltip>
					),
					show: hasAnyExceleRATEProduct(dataset.productId),
					accessor: "cErrorStatusId",
					filterable: true,
					maxWidth: 80,
					Cell: (rowInfo: any) => (
						<CoreRecommendationCell
							dateCompleted={rowInfo.original.coreScoreCompleted}
							testId={rowInfo.original.coreTestResultId}
							recommendation={rowInfo.row.cErrorStatusId}
						/>
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							closeDisplayFilter={closeFilter}
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							displayFilterName="displayCerrorFilters"
							shouldDisplayFilter={
								filterState.displayFilters.displayCerrorFilters &&
								filterState.showFilters
							}
							convertForFilter={setFilterValue}
							onChange={onChange}
							name="cErrorStatus"
							values={filterState.cErrorStatus}
						/>
					),
					filterMethod: (filter: any, row: any) =>
						setFilterMethodForSwitch(filter, row),
				},
				{
					Header: (
						<Tooltip title="T-Error">
							<span style={{ whiteSpace: "nowrap" }}>T-Error</span>
						</Tooltip>
					),
					show: hasAnyExceleRATEProduct(dataset.productId),
					accessor: "tErrorStatusId",
					filterable: true,
					maxWidth: 80,
					Cell: (rowInfo: any) => (
						<CoreRecommendationCell
							dateCompleted={rowInfo.original.coreScoreCompleted}
							testId={rowInfo.original.coreTestResultId}
							recommendation={rowInfo.row.tErrorStatusId}
						/>
					),
					Filter: ({ onChange }: { onChange: any }) => {
						const switchFilter = (
							<SwitchFilter
								convertForFilter={setFilterValue}
								closeDisplayFilter={closeFilter}
								showOrHideDisplayFilters={showOrHideDisplayFilter}
								displayFilterName="displayTerrorFilters"
								shouldDisplayFilter={
									filterState.displayFilters.displayTerrorFilters &&
									filterState.showFilters
								}
								onChange={onChange}
								name="tErrorStatus"
								values={filterState.tErrorStatus}
							/>
						);
						return (
							<Popover trigger="click" arrowPointAtCenter={true}>
								{switchFilter}
							</Popover>
						);
					},
					filterMethod: (filter: any, row: any) =>
						setFilterMethodForSwitch(filter, row),
				},
				{
					Header: <Tooltip title="Skills Appraisal">Skills Appraisal</Tooltip>,
					show: hasAnyExceleRATEProduct(dataset.productId),
					accessor: "appraisalStatusId",
					filterable: true,
					Cell: (rowInfo: any) => (
						<CoreRecommendationCell
							dateCompleted={rowInfo.original.coreScoreCompleted}
							testId={rowInfo.original.coreTestResultId}
							recommendation={rowInfo.row.appraisalStatusId}
						/>
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							closeDisplayFilter={closeFilter}
							convertForFilter={setFilterValue}
							displayFilterName="displayAppraisalFilters"
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							shouldDisplayFilter={
								filterState.displayFilters.displayAppraisalFilters &&
								filterState.showFilters
							}
							onChange={onChange}
							name="appraisalStatus"
							values={filterState.appraisalStatus}
						/>
					),
					filterMethod: (filter: any, row: any) =>
						setFilterMethodForSwitch(filter, row),
				},
				{
					Header: (
						<Tooltip title="Neurapulse Result">Neurapulse Result</Tooltip>
					),
					show: dataset.neurapulseProjectId !== undefined,
					accessor: "neurapulseState",
					filterable: true,
					Cell: (rowInfo: any) => (
						<NeurapulseResultCell neurapulseStatusId={rowInfo.row.neurapulseState} />
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<NeurapulseFilter
							closeDisplayFilter={closeFilter}
							convertForFilter={setNeurapulseFilterValue}
							displayFilterName="displayNeurapulseFilters"
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							shouldDisplayFilter={
								filterState.displayFilters.displayNeurapulseFilters &&
								filterState.showFilters
							}
							onChange={onChange}
							name="neurapulseStatus"
							values={filterState.neurapulseStatus}
						/>
					),
					filterMethod: (filter: any, row: any) =>
						setFilterMethodForNeurapulseSwitch(filter, row),
				},
				{
					Header: (
						<Tooltip title="Neurapulse Completed">Neurapulse Completed</Tooltip>
					),
					show: dataset.neurapulseProjectId !== undefined,
					accessor: "neurapulseCompleted",
					Cell: setDateColumnCellValue,
					filterMethod: dateFilterMethod("neurapulseCompleted"),
					Filter: ({ onChange }: any) => (
						<DateFilter
							accessor="neurapulseCompleted"
							onDateChange={onDateChange}
							date={filterState.neurapulseCompleted}
							onChange={onChange}
						/>
					),
					filterable: true,
				},
				{
					Header: onroadScoreToolTip(dataset.productId),
					show: hasAnyProduct(dataset.productId),
					filterable: true,
					accessor: "coreScore",
					maxWidth: 80,
					Cell: (rowInfo: any) => {
						if (rowInfo.row.coreScore) {
							return rowInfo.row.coreScore;
						} else if (rowInfo.original.coreScoreCompleted) {
							return "C";
						} else if (!rowInfo.original.coreTestResultId) {
							return "-";
						} else {
							return "NC";
						}
					},
				},
				{
					Header: (
						<Tooltip title="DORE Recommendation">DORE RECOMMENDATION</Tooltip>
					),
					show: hasDriveABLEProduct(dataset.productId),
					accessor: "onroadRecommendation",
					Cell: (rowInfo: any) => (
						<DoreRecommendationCell
							dateCompleted={rowInfo.original.coreScoreCompleted}
							testId={rowInfo.original.coreTestResultId}
							recommendation={rowInfo.row.onroadRecommendation}
						/>
					),
					Filter: ({ onChange }: { onChange: any }) => (
						<SwitchFilter
							closeDisplayFilter={closeFilter}
							convertForFilter={setFilterValue}
							displayFilterName="displayOnroadRecommendationFilters"
							showOrHideDisplayFilters={showOrHideDisplayFilter}
							shouldDisplayFilter={
								filterState.displayFilters.displayOnroadRecommendationFilters &&
								filterState.showFilters
							}
							onChange={onChange}
							name="onroadRecommendationStatus"
							values={filterState.onroadRecommendationStatus}
						/>
					),
					filterMethod: (filter: any, row: any) =>
						setFilterMethodForSwitch(filter, row),
				},
				{
					Header: <Tooltip title="Vehicle">Vehicle</Tooltip>,
					show: hasAnyProduct(dataset.productId),
					filterable: true,
					accessor: "vehicleType",
				},
				{
					Header: <Tooltip title="Course">Course</Tooltip>,
					show: hasAnyProduct(dataset.productId),
					accessor: "courseName",
					filterable: true,
					Cell: (rowInfo: any) => (
						<React.Fragment>{rowInfo.value}</React.Fragment>
					),
				},
				{
					Header: (
						<Tooltip title="Commercial Driving Experience">
							Commercial Driving Experience
						</Tooltip>
					),
					id: "monthsCommercialDrivingExp",
					accessor: "monthsCommercialDrivingExp",
					show:
						hasAnyExceleRATEProduct(dataset.productId) &&
						commercialDrivingExpSettings.showCommDrivingExp,
					Cell: (rowInfo: any) => {
						if (rowInfo.value !== undefined) {
							return <>{Utils.getCommercialDrivingExpDesc(rowInfo.value)}</>;
						} else {
							return <></>;
						}
					},
					filterable: true,
				},
				{
					Header: (
						<Tooltip title="Trip Inspection">Trip Inspection</Tooltip>
					),
					id: "hasTripInspection",
					accessor: "hasTripInspection",
					show: hasAnyExceleRATEProduct(dataset.productId),
					Cell: (rowInfo: any) => {
						if (rowInfo.value === true) {
							return <div>True</div>;
						} else {
							return <div>False</div>;
						}
					},
					filterable: true,
				},
			];

			const actionColumn = {
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				id: "id",
				accessor: "id",
				Filter: () => (
					<Button
						className="btn-outline-primary"
						onClick={() => {
							clearAll();
							setFiltered([{ id: "clientIsActive", value: "Active" }]);
						}}
					>
						Clear
					</Button>
				),
				Cell: (row: any) => (
					<React.Fragment>
						<IconButton
							className="p-1 m-0.5 btn-link"
							title="Edit Session"
							onClick={() => toggleOverlayOpen(row.original)}
							disabled={!enableEditAssessment(row.original)}
						>
							<Icon icon={SquarePen} />
						</IconButton>
						{row.original.allowToAccessReport && (
							<GenerateExceleRATEPdf
								fetchReportData={() =>
									fetchReportData({
										reportId: row.original.id,
										existingReportId: row.original.reportId,
										dataLocked: row.original.dataLocked,
										datasetProductId: dataset.productId,
										datasetId,
										organizationId: +organizationId,
										currentUser,
										commercialDrivingExpSettings,
										showEmployeeId: dataset.showClientParticipantId,
									})
								}
							/>
						)}
						{row.original.dataLocked && (
							<IconButton className="p-1 m-0.5 btn-link" title="locked">
								<Icon icon={Lock} />
							</IconButton>
						)}
						{row.original.dataLocked === false &&
							currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) && (
								<IconButton
									className="p-1 m-0.5 btn-link"
									title="Score Session"
									onClick={() => {
										setScoring(true);
										scoreAssessment(row.original.id);
									}}
									disabled={scoring}
								>
									<Icon icon={ClipboardCheck} />
								</IconButton>
							)}
					</React.Fragment>
				),
			};

			return [...columns, ...customColumns, actionColumn];
		},
		[
			dataset,
			commercialDrivingExpSettings,
			filterState,
			userCanSeeScores,
			organizationId,
			datasetId,
			currentUser,
			scoring,
			clearAll,
			setFiltered,
			toggleOverlayOpen,
			enableEditAssessment,
			fetchReportData,
			scoreAssessment,
			closeFilter,
			setFilterValue,
			showOrHideDisplayFilter,
			setFilterMethodForSwitch,
			setNeurapulseFilterValue,
			setFilterMethodForNeurapulseSwitch,
			onDateChange,
			convertDisplayOnValuesToLabelComponent,
		]
	);

	// ─── Render ────────────────────────────────────────────────────────────────────
	const assessmentColumns = getColumns(customFields);
	const timestamp = getTime("date");
	const csvExportFilename = `${dataset.name} Results - ${timestamp}.csv`;
	const hasResults = csvTestResults && csvTestResults.csvResults.length > 0;
	const hasFilteredResults =
		csvTestResultsFiltered && csvTestResultsFiltered.csvResults.length > 0;
	const showExportButton = hasResults || hasFilteredResults;
	const dp = getDatasetPermissions(currentUser, datasetId);

	if (isLoading) return <Spinner size="full-page" />;

	return (
		<Page title="Dataset Test Results">
			<PageHero>
				<h1 className="mb-0">Test Results</h1>
			</PageHero>
			<div className="container-fluid">
				<div className="d-flex">
					<div className="my-3">
						<div>C = Completed, NC = Not Completed</div>
					</div>
					<div className="ml-auto">
						{showExportButton && (
							<Dropdown>
								<Dropdown.Toggle variant="primary" id="dropdown-split-basic">
									Export Results &nbsp;
									<Icon icon={FileSpreadsheet} />
								</Dropdown.Toggle>
								<Dropdown.Menu>
									{hasResults ? (
										<CSVLink
											className="btn"
											data={csvTestResults.csvResults}
											headers={csvTestResults.headers}
											filename={csvExportFilename}
										>
											Export All Results
										</CSVLink>
									) : null}
									{hasFilteredResults ? (
										<CSVLink
											className="btn"
											data={csvTestResultsFiltered.csvResults}
											headers={csvTestResultsFiltered.headers}
											filename={csvExportFilename}
										>
											Export Filtered Results
										</CSVLink>
									) : null}
								</Dropdown.Menu>
							</Dropdown>
						)}
					</div>
				</div>
				<div>
					<DataTable
						data={results}
						columns={assessmentColumns}
						noDataText="There are no results available"
						filterable={true}
						filtered={filtered}
						ref={reactTable}
						onFilteredChange={(f: any[]) => {
							setFiltered(f);
						}}
						onDataFiltered={onDataFiltered}
						defaultSorted={[{ id: "dateCreated", desc: true }]}
						storageKey={"DatasetAssessmentResultsGrid"}
					/>
					{syncSrc ? (
						<CustomProtocolHandler
							src={syncSrc}
							onerror={handleCustomProtocolError}
						/>
					) : null}
					<ClientResultModalOverlay
						vitalsDateCompleted={
							selectedAssessmentResult
								? selectedAssessmentResult.vitalsDateCompleted
								: undefined
						}
						withAssessment={selectedAssessmentResult ? true : false}
						closeModal={closeModal}
						modalOpen={isModalOpen}
						existingAssessmentResultCloseModal={closeModal}
						customFieldNotValidated={checkIfCustomFieldValueIsSuccess(
							validNewTestValidation
						)}
						selectedPurposeId={
							selectedAssessmentResult
								? selectedAssessmentResult.assessmentPurposeId
								: newTestPurposeId
						}
						onSelectedPurposeChange={(v: number) => {
							if (selectedAssessmentResult) {
								selectedAssessmentResult.assessmentPurposeId = v;
								setSelectedAssessmentResult({ ...selectedAssessmentResult });
							} else {
								setNewTestPurposeId(v);
							}
						}}
						coreButtonProps={{
							licensedProductContainsCore: hasAnyProduct(dataset.productId),
							dataLocked:
								selectedAssessmentResult &&
								selectedAssessmentResult.dataLocked !== undefined
									? selectedAssessmentResult.dataLocked
									: false,
							coreTestResultId:
								selectedAssessmentResult &&
								selectedAssessmentResult.coreTestResultId !== undefined
									? selectedAssessmentResult.coreTestResultId
									: undefined,
							getCoreTest: getCoreTestResult,
							startCore: startCoreTest,
							canEditCore:
								currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
								datasetModules.find(
									(m) => m.id === ModuleType.CORE || m.id === ModuleType.DORE
								),
						}}
						vitalButtonProps={{
							licensedProductContainsVitals: hasExceleRATEV1OnlyProduct(
								dataset.productId
							),
							dataLocked:
								selectedAssessmentResult &&
								selectedAssessmentResult.dataLocked !== undefined
									? selectedAssessmentResult.dataLocked
									: false,
							vitalsId:
								selectedAssessmentResult &&
								selectedAssessmentResult.vitalsTestResultId !== undefined
									? selectedAssessmentResult.vitalsTestResultId
									: undefined,
							vitalsScore:
								selectedAssessmentResult &&
								selectedAssessmentResult.vitalsScore !== undefined
									? selectedAssessmentResult.vitalsScore
									: undefined,
							DCATButtonClicked: DCATButtonClicked,
							DCATRunning: DCATRunning,
							getVitalsTest: getVitalsTestResult,
							resumeVitalsTest: resumeVitalsTest,
							startVitalsTest: startVitalsTest,
							vitalsLaunched: vitalsLaunched,
							showLaunchedMessageHandle: showLaunchMessageVisible,
							canSync: canSync,
							canEditVitals:
								currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
								(dp &&
									dp.permissions &&
									dp.permissions.includes(ClientPermissions.EditVitalsTest) &&
									currentUser.vitalsCertified),
						}}
						vitalsSummaryProps={{
							careTestResultId:
								selectedAssessmentResult && selectedAssessmentResult !== undefined
									? selectedAssessmentResult.careTestResultId
									: undefined,
							dataLocked:
								selectedAssessmentResult &&
								selectedAssessmentResult.dataLocked !== undefined
									? selectedAssessmentResult.dataLocked
									: false,
							canEditCare:
								currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
								(dp &&
									dp.permissions &&
									dp.permissions.includes(ClientPermissions.EditCareTest) &&
									currentUser.careCertified),
							getVitalsSummary: () => {
								if (
									selectedAssessmentResult &&
									selectedAssessmentResult !== undefined
								) {
									navigateToVitalsSummary(
										selectedAssessmentResult.id,
										selectedAssessmentResult.careTestResultId
									);
								}
							},
						}}
						customFieldProps={{
							datasetId: +datasetId,
							organizationId: +organizationId,
							pickListItems: context.pickListItems,
							customFieldTypes: ["AssessmentType"],
							customFieldTypeIds:
								selectedAssessmentResult !== undefined
									? {
											typeOfCustomFieldValue: "AssessmentType",
											value: selectedAssessmentResult.id!,
									  }
									: undefined,
							getCustomFieldValidation: getNewTestAssessmentCustomFieldValidation,
							getCustomFieldValueFromState: getAssessmentCustomFieldValues,
						}}
						assessmentProps={{
							assessmentResultId:
								selectedAssessmentResult !== undefined
									? selectedAssessmentResult.id
										? selectedAssessmentResult.id
										: 0
									: 0,
							organizationId: +organizationId,
							datasetId: datasetId,
							clientId: selectedAssessmentResult && selectedAssessmentResult.clientId
								? selectedAssessmentResult.clientId
								: 0,
							neurapulseEnabled: dataset.neurapulseProjectId !== undefined,
							hasNeurapulseResult:
								selectedAssessmentResult !== undefined &&
								selectedAssessmentResult.neurapulseCompleted
									? true
									: false,
							onAssessmentCreated: onAssessmentCreated,
							onCustomFieldValuesUpdated: onCustomFieldValuesUpdated,
						}}
						productType={dataset.productId}
					/>
				</div>
			</div>
		</Page>
	);
};

export default DatasetAssessmentResults;
